import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { RepCoachingGoals } from '../types';

export interface CreateRepCoachingGoalsParams {
	userId: string;
	workingOn: string;
	tysonMaxDifficulty: number;
}

export interface UpdateRepCoachingGoalsParams {
	id: string;
	workingOn: string;
	tysonMaxDifficulty: number;
}

/**
 * Hook for creating rep coaching goals
 */
export function useCreateRepCoachingGoals() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			userId,
			workingOn,
			tysonMaxDifficulty,
		}: CreateRepCoachingGoalsParams): Promise<RepCoachingGoals> => {
			const requestData = {
				userId,
				workingOn,
				tysonMaxDifficulty,
			};

			const response = await userSession.webServiceHelper.callWebServiceAsync<RepCoachingGoals>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'aida/repCoachingGoals',
				}),
				'POST',
				requestData
			);

			if (!response.value) {
				throw new Error('No data returned from create rep coaching goals API');
			}

			return response.value;
		},
		onSuccess: (data, variables) => {
			// Invalidate and refetch the coaching goals for this user
			queryClient.invalidateQueries({ queryKey: ['repCoachingGoals', variables.userId] });
		},
		onError: (error: Error) => {
			console.error('Failed to create rep coaching goals:', error);
		},
	});
}

/**
 * Hook for updating rep coaching goals
 */
export function useUpdateRepCoachingGoals() {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			id,
			workingOn,
			tysonMaxDifficulty,
		}: UpdateRepCoachingGoalsParams): Promise<RepCoachingGoals> => {
			const requestData = {
				workingOn,
				tysonMaxDifficulty,
			};

			const response = await userSession.webServiceHelper.callWebServiceAsync<RepCoachingGoals>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `aida/repCoachingGoals/${id}`,
				}),
				'PUT',
				requestData
			);

			if (!response.value) {
				throw new Error('No data returned from update rep coaching goals API');
			}

			return response.value;
		},
		onSuccess: data => {
			// Invalidate and refetch the coaching goals for this user
			if (data.userId) {
				queryClient.invalidateQueries({ queryKey: ['repCoachingGoals', data.userId] });
			}
		},
		onError: (error: Error) => {
			console.error('Failed to update rep coaching goals:', error);
		},
	});
}

/**
 * Combined hook that provides both mutations
 */
export function useRepCoachingGoalsMutations() {
	const createMutation = useCreateRepCoachingGoals();
	const updateMutation = useUpdateRepCoachingGoals();

	return {
		create: createMutation,
		update: updateMutation,
	};
}
